import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';

import { usePersonaFechamento } from '../fechamento-v2/persona';
import { useMedicoesSme } from './dados';

/**
 * Abas do "Ciclo mensal" do portal SME: Fechamento → Medições → Demonstrativos.
 *
 * Migradas do protótipo (`CicloMensalTabs.tsx`). Lá Medições e Demonstrativos
 * saíram do menu lateral porque estas abas os absorveram; aqui é igual — o
 * menu só tem "Fechamento", e as duas rotas são alcançadas por estas abas
 * (registradas com motivo em `ROTAS_FORA_DO_MENU`, `nucleo/navegacao/mapa.ts`).
 *
 * TRÊS DIVERGÊNCIAS CONSCIENTES EM RELAÇÃO AO PROTÓTIPO:
 *
 * 1. **São links, não `Tabs` do Radix.** Cada aba troca de rota; não há painel
 *    de aba na mesma página. `role="tab"` sem `tabpanel` é papel mentiroso para
 *    leitor de tela — aqui é um `<nav>` com `aria-current="page"` na ativa, com
 *    o mesmo desenho visual das abas do design system.
 * 2. **A faixa não é fixa no rolar.** O protótipo a prende sob um cabeçalho de
 *    altura fixa (`top-14`); o cabeçalho da casca daqui tem altura variável (as
 *    migalhas), e prender a faixa num valor chutado a esconderia atrás dele.
 * 3. **A persona DRE não vê a aba Medições nem o selo de recusados.** O
 *    protótipo guarda `/sme/medicoes` com `ProtectedSmeOnlyRoute` (visão de
 *    todas as DREs) e mostra a aba mesmo assim, que só redireciona; aqui a aba
 *    nem é renderizada (CLAUDE.md, regras de interface, item 7). O selo conta
 *    recusados de todas as diretorias, e o sistema não sabe de qual diretoria é
 *    quem está olhando — mesma regra do Painel e das Tratativas.
 */

interface Aba {
  rotulo: string;
  caminho: string;
  dica: string;
}

const FECHAMENTO: Aba = {
  rotulo: 'Fechamento',
  caminho: '/sme/fechamento-mensal',
  dica: 'Apontamento mensal por OS — base de cálculo da medição (faltas, ajustes, atividades contínuas) antes do envio à SME.',
};
const MEDICOES: Aba = {
  rotulo: 'Medições',
  caminho: '/sme/medicoes',
  dica: 'Acompanhamento das medições consolidadas por DRE e credenciado — valores apurados e status de aprovação.',
};
const DEMONSTRATIVOS: Aba = {
  rotulo: 'Demonstrativos',
  caminho: '/sme/demonstrativos',
  dica: 'Demonstrativos formais por credenciado (PF, PJ e cooperativa) — espelho do cálculo enviado para validação.',
};

/** Selo com teto, como o `formatBadgeCount` do protótipo. */
function contagemDoSelo(quantos: number): string {
  return quantos > 99 ? '99+' : String(quantos);
}

export function CicloMensalAbas() {
  const { pathname } = useLocation();
  const persona = usePersonaFechamento();
  const ehDre = persona === 'DRE';
  const { data: medicoes } = useMedicoesSme();

  const recusados = ehDre ? 0 : (medicoes ?? []).filter((m) => m.recusadoPeloCredenciado).length;
  const abas = ehDre ? [FECHAMENTO, DEMONSTRATIVOS] : [FECHAMENTO, MEDICOES, DEMONSTRATIVOS];

  return (
    <nav aria-label="Ciclo mensal" className="max-w-full overflow-x-auto">
      <ul className="flex w-max list-none items-center gap-1 rounded-2xl border border-border/40 bg-muted/50 p-1 text-muted-foreground">
        {abas.map((aba) => {
          const ativa = pathname.startsWith(aba.caminho);
          const comSelo = aba === DEMONSTRATIVOS && recusados > 0;
          const dica = comSelo
            ? `${aba.dica} ${recusados} recusado(s) pelo credenciado — requer tratativa.`
            : aba.dica;
          return (
            <li key={aba.caminho}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={aba.caminho}
                    aria-current={ativa ? 'page' : undefined}
                    className={cn(
                      'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200 hover:bg-background/50 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      ativa && 'bg-card text-foreground shadow-xs dark:bg-primary/10',
                    )}
                  >
                    {aba.rotulo}
                    {comSelo && (
                      <Badge
                        variant="destructive"
                        className="h-5 px-1.5 text-[10px] font-semibold"
                        aria-label={`${recusados} demonstrativo(s) recusado(s)`}
                      >
                        {contagemDoSelo(recusados)}
                      </Badge>
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  {dica}
                </TooltipContent>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
